import React__default, { useState, useEffect } from 'react';
import { StyleSheet, View, Platform, useWindowDimensions, Dimensions, Image, KeyboardAvoidingView } from 'react-native';
import Auth$1, { Auth } from '@aws-amplify/auth';
import { A as AppFormCodeInput } from './AppFormCodeInput-87bc5844.esm.js';
import { useTranslation } from 'react-i18next';
import { A as AppText } from './index-6b88191c.esm.js';
import { A as AppForm, a as AppSubmitButton } from './AppSubmitButton-2e6a9268.esm.js';
import { A as AppFormField } from './AppFormField-e5fa211c.esm.js';
import { u as useTheme } from './useTheme-90e8e2c2.esm.js';
import { useToast } from 'react-native-toast-notifications';
import * as Yup from 'yup';
import { A as AppAssets } from './index-3256db5b.esm.js';
import { A as AppButton } from './AppButton-a3bdb006.esm.js';

const useAuth = function (config, onCognitoUserAuthenticated) {
  let viewOnNotAuth = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : "auth";
  if (config) Auth.configure(config);
  const [currentView, setCurrentView] = useState("initializing");
  const checkAuth = async () => {
    try {
      const session = await Auth.currentSession();
      onCognitoUserAuthenticated && onCognitoUserAuthenticated(session);
      setCurrentView("mainNav");
    } catch (err) {
      console.error("user is not signed in " + err);
      setCurrentView(viewOnNotAuth);
    }
  };
  useEffect(() => {
    var _window, _window2, _window2$electronAPI;
    if ((_window = window) !== null && _window !== void 0 && _window.electronAPI && (_window2 = window) !== null && _window2 !== void 0 && (_window2$electronAPI = _window2.electronAPI) !== null && _window2$electronAPI !== void 0 && _window2$electronAPI.handleTokens) {
      window.electronAPI.handleTokens(tokens => {
        console.log("Renderer: Received tokens from main process:", tokens);
        if (tokens) ; else {
          checkAuth();
        }
      });
    } else {
      checkAuth();
    }
  }, []);
  const getCurrentAuthenticatedUser = () => {
    return Auth.currentAuthenticatedUser();
  };
  const getCurrentSession = () => {
    return Auth.currentSession();
  };
  const signOut = async () => {
    try {
      const result = await Auth.signOut();
      console.log("Auth - user successfully signed out");
      return result;
    } catch (error) {
      console.error("Auth - error signing out: ", error);
    }
  };
  const attemptSignInWithGoogle = () => {
    return Auth.federatedSignIn({
      provider: "Google"
    });
  };
  return {
    currentView,
    setCurrentView,
    getCurrentAuthenticatedUser,
    getCurrentSession,
    signOut,
    attemptSignInWithGoogle,
    checkAuth
  };
};

var Validations = {
  loginSchema: Yup.object().shape({
    // username: Yup.string().required().label("Username"),
    email: Yup.string().required().email().label("Email"),
    password: Yup.string().required().min(8).label("Password")
  }),
  ssoSchema: Yup.object().shape({
    email: Yup.string().required().email().label("Email")
  }),
  forgotPasswordStage0: Yup.object().shape({
    email: Yup.string().required().email().label("Email")
  }),
  forgotPasswordStage1: Yup.object().shape({
    authorizationCode: Yup.string().required().matches(/^[0-9]+$/, "Must be only digits").min(6, "Must be exactly 6 digits").max(6, "Must be exactly 6 digits").label("Auth code"),
    password: Yup.string().required().min(8).label("New Password"),
    passwordConfirmation: Yup.string().oneOf([Yup.ref("password")], "Passwords must match")
  })
};

const ForgotPassword = _ref => {
  var _currentTheme$theme, _currentTheme$theme2, _currentTheme$theme3, _currentTheme$theme4, _currentTheme$theme5, _currentTheme$theme6, _currentTheme$theme7, _currentTheme$theme8;
  let {
    toggleAuthType,
    AuthButton,
    showCode,
    user
  } = _ref;
  const {
    currentTheme
  } = useTheme();
  const {
    t
  } = useTranslation();
  const [isResetRequestInProgress, setIsResetRequestInProgress] = useState(false);
  const [isResetConfirmInProgress, setIsResetConfirmInProgress] = useState(false);
  const [state, setState] = useState({
    stage: showCode ? 0 : 1,
    username: "",
    email: "",
    password: "",
    authorizationCode: showCode ? "" : 123456
  });
  const AppToasty = useToast();
  const resetPassword = async values => {
    try {
      if (isResetRequestInProgress) return AppToasty.show(t("passwordChangeRequestInProgressCaption"), {
        type: "warning"
      });
      setIsResetRequestInProgress(true);
      const result = await Auth.forgotPassword(values.email);
      setState({
        ...state,
        stage: 1,
        username: values.email
      });
      AppToasty.show(t("checkInboxCaption"), {
        type: "success"
      });
    } catch (error) {
      console.error("error: ", error);
      AppToasty.show(error.message, {
        type: "danger"
      });
    } finally {
      setIsResetRequestInProgress(false);
    }
  };
  const confirmResetPassword = values => {
    if (isResetConfirmInProgress) return AppToasty.show(t("passwordChangeCaption"), {
      type: "warning"
    });
    setIsResetConfirmInProgress(true);
    const {
      username
    } = state;
    const {
      password,
      authorizationCode
    } = values;
    if (showCode) {
      Auth.forgotPasswordSubmit(username, authorizationCode, password).then(() => {
        AppToasty.show(t("passwordChangedCaption"), {
          type: "success"
        });
        toggleAuthType("showSignIn");
      }).catch(err => {
        console.error("error resetting password:", err);
        AppToasty.show(t("passwordChangeFailedCaption"), {
          type: "danger"
        });
      }).finally(() => {
        setIsResetConfirmInProgress(false);
      });
    } else {
      Auth.completeNewPassword(user, password, {}).then(() => {
        AppToasty.show(t("passwordChangedCaption"), {
          type: "success"
        });
        toggleAuthType("showSignIn");
      }).catch(err => {
        console.error("error resetting password:", err);
        AppToasty.show(t("passwordChangeFailedCaption"), {
          type: "danger"
        });
      }).finally(() => {
        setIsResetConfirmInProgress(false);
      });
    }
  };
  const {
    stage
  } = state;
  return /*#__PURE__*/React__default.createElement(View, {
    style: {
      alignContent: "center"
    }
  }, stage === Number(0) && /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      email: ""
    },
    onSubmit: values => resetPassword(values),
    validationSchema: Validations.forgotPasswordStage0
  }, /*#__PURE__*/React__default.createElement(AppText, {
    wrapperStyle: styles$5.titleLabel,
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme = currentTheme.theme) === null || _currentTheme$theme === void 0 ? void 0 : _currentTheme$theme.small_font_size,
      textAlign: "center"
    },
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme2 = currentTheme.theme) === null || _currentTheme$theme2 === void 0 ? void 0 : _currentTheme$theme2.mediumGrey
  }, "emailRegistrationCaption"), /*#__PURE__*/React__default.createElement(AppFormField, {
    name: "email",
    placeholder: "Email",
    autoCapitalize: "none",
    keyboardtype: "email-address",
    textContextType: "emailAddress",
    autoCorrect: false
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    isDisabled: isResetConfirmInProgress,
    backgroundColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme3 = currentTheme.theme) === null || _currentTheme$theme3 === void 0 ? void 0 : _currentTheme$theme3.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme4 = currentTheme.theme) === null || _currentTheme$theme4 === void 0 ? void 0 : _currentTheme$theme4.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }],
    title: "Get authorisation code"
  })), stage === Number(1) && /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      authorizationCode: showCode ? "" : 123456,
      password: ""
    },
    onSubmit: values => confirmResetPassword(values),
    validationSchema: Validations.forgotPasswordStage1
  }, /*#__PURE__*/React__default.createElement(AppText, {
    wrapperStyle: [styles$5.titleLabel, {
      marginBottom: 0
    }],
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme5 = currentTheme.theme) === null || _currentTheme$theme5 === void 0 ? void 0 : _currentTheme$theme5.small_font_size
    },
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme6 = currentTheme.theme) === null || _currentTheme$theme6 === void 0 ? void 0 : _currentTheme$theme6.mediumGrey
  }, showCode ? "insertCodeAndPasswordCaption" : "insertPasswordCaption"), showCode && /*#__PURE__*/React__default.createElement(AppFormCodeInput, {
    placeholder: "Authorisation code",
    autoCapitalize: "none"
    // keyboardtype="email-address"
    ,
    autoCorrect: false,
    name: "authorizationCode",
    rootStyle: {
      marginBottom: 20
    }
  }), /*#__PURE__*/React__default.createElement(AppFormField, {
    autoComplete: Platform.OS === "web" ? "none" : "off",
    name: "password",
    autoCapitalize: "none",
    textContextType: "Name",
    placeholder: "New Password",
    secureTextEntry: true,
    autoCorrect: false
  }), /*#__PURE__*/React__default.createElement(AppFormField, {
    autoComplete: Platform.OS === "web" ? "none" : "off",
    placeholder: "Confirm Password",
    autoCapitalize: "none",
    secureTextEntry: true,
    textContextType: "Name",
    autoCorrect: false,
    name: "passwordConfirmation"
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    backgroundColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme7 = currentTheme.theme) === null || _currentTheme$theme7 === void 0 ? void 0 : _currentTheme$theme7.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    isDisabled: isResetConfirmInProgress,
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme8 = currentTheme.theme) === null || _currentTheme$theme8 === void 0 ? void 0 : _currentTheme$theme8.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }],
    title: "Reset Password"
  })));
};
const styles$5 = StyleSheet.create({
  titleLabel: {
    marginBottom: 20,
    marginTop: 10,
    alignSelf: "center",
    maxWidth: "100%",
    fontWeight: "bold",
    textAlign: "center"
  }
});
ForgotPassword.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "ForgotPassword"
};

const HorizontalSeparator = _ref => {
  let {
    text
  } = _ref;
  const {
    currentTheme
  } = useTheme();
  const styles = generateStyles(currentTheme);
  return /*#__PURE__*/React__default.createElement(View, {
    style: styles.separatorContainer
  }, /*#__PURE__*/React__default.createElement(View, {
    style: styles.line
  }), /*#__PURE__*/React__default.createElement(View, {
    style: styles.textContainer
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: styles.text
  }, text)), /*#__PURE__*/React__default.createElement(View, {
    style: styles.line
  }));
};
const generateStyles = currentTheme => {
  var _currentTheme$theme, _currentTheme$theme2;
  return StyleSheet.create({
    separatorContainer: {
      flexDirection: "row",
      alignItems: "center",
      marginTop: 20 // Adjust for space around the separator
    },

    line: {
      flex: 1,
      height: 1,
      // Adjust thickness of the line
      backgroundColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme = currentTheme.theme) === null || _currentTheme$theme === void 0 ? void 0 : _currentTheme$theme.lightGrey,
      width: 150 // Adjust line color
    },

    textContainer: {
      paddingHorizontal: 10 // Adjust for space on the sides of the text
    },

    text: {
      fontSize: 16,
      color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme2 = currentTheme.theme) === null || _currentTheme$theme2 === void 0 ? void 0 : _currentTheme$theme2.mediumGrey
    }
  });
};
HorizontalSeparator.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "HorizontalSeparator"
};

const SignIn = _ref => {
  var _currentTheme$theme, _currentTheme$theme2, _currentTheme$theme3, _currentTheme$theme4, _currentTheme$theme5, _currentTheme$theme6, _currentTheme$theme7, _currentTheme$theme8, _currentTheme$theme9, _currentTheme$theme10, _currentTheme$theme11, _currentTheme$theme12, _currentTheme$theme13, _currentTheme$theme14, _currentTheme$theme15, _currentTheme$theme16;
  let {
    iconApple = AppAssets.social.apple,
    iconEmail = AppAssets.social.create,
    iconFacebook = AppAssets.social.facebook,
    iconGoogle = AppAssets.social.google,
    iconMicrosoft = AppAssets.social.microsoft,
    iconSignIn,
    isSignInWithGoogleEnabled,
    isSignInWithAppleEnabled,
    isSignInWithFacebookEnabled,
    toggleAuthType,
    AuthButton,
    setUser,
    setFederatedSignInProgress,
    isSSOwithAzureAD
  } = _ref;
  const {
    currentTheme
  } = useTheme();
  const styles = styleGenerator$1();
  const signUp = () => {
    toggleAuthType("showSignUp");
  };
  React__default.useEffect(() => {
    const currentURL = window.location.href;
    if (currentURL.includes("google_signup")) {
      handleSignInWithGoogle();
    }
  }, [window.location.href]);
  const handleSignInWithGoogle = async () => {
    setFederatedSignInProgress(true);
    Auth.federatedSignIn({
      provider: "Google"
    }).then(user => {
      console.log("fed_user", user);
    }).catch(err => {
      console.log("fed_user_login_error", err);
    });
  };
  return /*#__PURE__*/React__default.createElement(View, {
    style: styles.container
  }, /*#__PURE__*/React__default.createElement(View, {
    style: {
      flex: 1,
      justifyContent: "center",
      alignItems: "center",
      flexDirection: "column"
    }
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme = currentTheme.theme) === null || _currentTheme$theme === void 0 ? void 0 : _currentTheme$theme.small_font_size
    },
    wrapperStyle: styles.titleLabel,
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme2 = currentTheme.theme) === null || _currentTheme$theme2 === void 0 ? void 0 : _currentTheme$theme2.mediumGrey,
    fontWeight: "bold",
    preventTranslation: true
  }, "Create powerful AI Agents tailored to your organisation, privately and securely."), /*#__PURE__*/React__default.createElement(AppButton, {
    accessibilityLabel: "sign-in-init-button",
    title: "Sign in",
    labelStyle: {
      color: "rgba(0, 0, 0, .75)"
    },
    icon: iconSignIn,
    onPress: () => toggleAuthType("showSignInInputs"),
    backgroundColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme3 = currentTheme.theme) === null || _currentTheme$theme3 === void 0 ? void 0 : _currentTheme$theme3.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    style: [{
      height: 35,
      minWidth: 218,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme4 = currentTheme.theme) === null || _currentTheme$theme4 === void 0 ? void 0 : _currentTheme$theme4.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }]
    // backgroundColor="linear-gradient(#e66465, #9198e5)"
  }), /*#__PURE__*/React__default.createElement(HorizontalSeparator, {
    text: "or"
  }), isSignInWithGoogleEnabled && /*#__PURE__*/React__default.createElement(AuthButton, {
    title: "Sign in with Google  ",
    labelStyle: {
      color: "rgba(0, 0, 0, .75)"
    },
    fontColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme5 = currentTheme.theme) === null || _currentTheme$theme5 === void 0 ? void 0 : _currentTheme$theme5.black
    // borderColor={currentTheme?.theme?.darkGrey}
    ,
    icon: iconGoogle,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 218
    }],
    btnContentStyle: {
      paddingLeft: 4,
      paddingRight: 10
    },
    onPress: handleSignInWithGoogle,
    backgroundColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme6 = currentTheme.theme) === null || _currentTheme$theme6 === void 0 ? void 0 : _currentTheme$theme6.white,
    style: [{
      height: 35,
      minWidth: 218,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      marginHorizontal: 10,
      boxShadow: "rgba(0, 0, 0, 0.1) 3px 3px 4.84px"
    }]
  }), isSSOwithAzureAD && /*#__PURE__*/React__default.createElement(AuthButton, {
    title: "Sign in with Microsoft",
    labelStyle: {
      color: "rgba(0, 0, 0, .75)"
    },
    fontColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme7 = currentTheme.theme) === null || _currentTheme$theme7 === void 0 ? void 0 : _currentTheme$theme7.black
    // borderColor={currentTheme?.theme?.darkGrey}
    ,
    icon: iconMicrosoft,
    iconStyle: {
      height: 16,
      width: 16,
      marginLeft: 13,
      marginRight: 7
    },
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 218
    }],
    btnContentStyle: {
      paddingLeft: 4,
      paddingRight: 10
    },
    onPress: async () => {
      toggleAuthType("showSSO_SignInInputs");
    },
    backgroundColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme8 = currentTheme.theme) === null || _currentTheme$theme8 === void 0 ? void 0 : _currentTheme$theme8.white,
    style: [{
      height: 35,
      minWidth: 218,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      marginHorizontal: 10,
      boxShadow: "rgba(0, 0, 0, 0.1) 3px 3px 4.84px"
    }]
  }), isSignInWithAppleEnabled && /*#__PURE__*/React__default.createElement(AuthButton, {
    title: "Sign in with Apple",
    onPress: () => Auth.federatedSignIn({
      provider: "SignInWithApple"
    }),
    backgroundColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme9 = currentTheme.theme) === null || _currentTheme$theme9 === void 0 ? void 0 : _currentTheme$theme9.true_black,
    fontColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme10 = currentTheme.theme) === null || _currentTheme$theme10 === void 0 ? void 0 : _currentTheme$theme10.white,
    icon: iconApple,
    style: styles.buttonContainer
  }), isSignInWithFacebookEnabled && /*#__PURE__*/React__default.createElement(AuthButton, {
    title: "Sign in with Facebook",
    onPress: () => Auth.federatedSignIn({
      provider: "Facebook"
    }),
    backgroundColor: "#39579A",
    fontColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme11 = currentTheme.theme) === null || _currentTheme$theme11 === void 0 ? void 0 : _currentTheme$theme11.white,
    icon: iconFacebook,
    style: styles.buttonContainer
  })), /*#__PURE__*/React__default.createElement(View, {
    style: {
      marginTop: 20,
      alignItems: "center",
      alignContent: "center",
      position: "relative",
      top: 20
    }
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme12 = currentTheme.theme) === null || _currentTheme$theme12 === void 0 ? void 0 : _currentTheme$theme12.small_font_size
    },
    wrapperStyle: styles.separatorLabel,
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme13 = currentTheme.theme) === null || _currentTheme$theme13 === void 0 ? void 0 : _currentTheme$theme13.mediumGrey
  }, "notRegisteredYet"), /*#__PURE__*/React__default.createElement(AppText, {
    isClickable: true,
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme14 = currentTheme.theme) === null || _currentTheme$theme14 === void 0 ? void 0 : _currentTheme$theme14.small_font_size,
      textAlign: "center",
      color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme15 = currentTheme.theme) === null || _currentTheme$theme15 === void 0 ? void 0 : _currentTheme$theme15.primary,
      fontWeight: "bold"
    },
    wrapperStyle: styles.separatorLabel,
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme16 = currentTheme.theme) === null || _currentTheme$theme16 === void 0 ? void 0 : _currentTheme$theme16.primary,
    preventTranslation: true,
    fontWeight: "bold",
    onPress: signUp
  }, "Please click here to sign up")));
};
const styleGenerator$1 = props => StyleSheet.create({
  buttonContainer: {
    marginTop: 10
  },
  container: {
    justifyContent: "space-between",
    flex: 1,
    marginLeft: "auto",
    marginRight: "auto",
    marginTop: 10

    // alignItems: "center",
  },

  separatorLabel: {
    marginBottom: 0,
    marginTop: 10,
    alignSelf: "center"
  },
  titleLabel: {
    marginBottom: 0,
    marginTop: 10,
    alignSelf: "center",
    maxWidth: "100%",
    fontWeight: "bold",
    textAlign: "center"
  }
});
SignIn.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "SignIn",
  "props": {
    "iconApple": {
      "defaultValue": {
        "value": "Assets.social.apple",
        "computed": true
      },
      "required": false
    },
    "iconEmail": {
      "defaultValue": {
        "value": "Assets.social.create",
        "computed": true
      },
      "required": false
    },
    "iconFacebook": {
      "defaultValue": {
        "value": "Assets.social.facebook",
        "computed": true
      },
      "required": false
    },
    "iconGoogle": {
      "defaultValue": {
        "value": "Assets.social.google",
        "computed": true
      },
      "required": false
    },
    "iconMicrosoft": {
      "defaultValue": {
        "value": "Assets.social.microsoft",
        "computed": true
      },
      "required": false
    }
  }
};

const SignInInputs = _ref => {
  var _currentTheme$theme, _currentTheme$theme2, _currentTheme$theme3, _currentTheme$theme4, _currentTheme$theme5, _currentTheme$theme6, _currentTheme$theme7, _currentTheme$theme8;
  let {
    onSignIn,
    toggleAuthType,
    isAsyncProcessInProgress,
    setIsAsyncProcessInProgress,
    setUser
  } = _ref;
  const AppToasty = useToast();
  const {
    t
  } = useTranslation();
  const {
    currentTheme
  } = useTheme();
  const signIn = async values => {
    if (isAsyncProcessInProgress) return;
    setIsAsyncProcessInProgress(true);
    const {
      email,
      password
    } = values;
    try {
      var _window, _window2, _window2$electronAPI;
      const result = await Auth.signIn(email, password);
      console.log("Auth - successfully signed in", result);
      if (result.challengeName === "NEW_PASSWORD_REQUIRED") {
        setUser(result);
        toggleAuthType("showForgotPasswordNoCode");
        AppToasty.show(t("SignInCaptionChangePassword"), {
          placement: "top",
          type: "success"
        });
        return;
      }
      if ((_window = window) !== null && _window !== void 0 && _window.electronAPI && (_window2 = window) !== null && _window2 !== void 0 && (_window2$electronAPI = _window2.electronAPI) !== null && _window2$electronAPI !== void 0 && _window2$electronAPI.storeTokens) {
        try {
          const electronTokens = await Auth.currentSession();
          window.electronAPI.storeTokens(electronTokens);
          console.log("Electron tokens stored", electronTokens);
          setTimeout(() => {
            console.log("_____");
          }, [5000]);
        } catch (error) {
          console.error("Renderer: Error processing login success:", error);
        }
      }
      onSignIn(result);
      AppToasty.show(t("SignInCaption"), {
        placement: "top",
        type: "success"
      });
      window.location.reload(true);
    } catch (error) {
      console.log("Auth - error signing in...", error);
      AppToasty.show(t("SignInFailedCaption") + (error === null || error === void 0 ? void 0 : error.message), {
        placement: "top",
        type: "danger"
      });
    } finally {
      setIsAsyncProcessInProgress(false);
    }
  };
  const showForgotPassword = () => {
    toggleAuthType("showForgotPassword");
  };
  return /*#__PURE__*/React__default.createElement(View, {
    style: styles$4.container
  }, /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      username: "",
      email: "",
      password: ""
    },
    onSubmit: values => signIn(values),
    validationSchema: Validations.loginSchema
  }, /*#__PURE__*/React__default.createElement(AppFormField, {
    accessibilityLabel: "email",
    accessible: true,
    placeholder: "Email",
    autoCapitalize: "none",
    keyboardtype: "email-address",
    textContextType: "emailAddress",
    autoCorrect: false,
    name: "email"
  }), /*#__PURE__*/React__default.createElement(AppFormField, {
    accessibilityLabel: "password",
    accessible: true,
    placeholder: "Password",
    autoCapitalize: "none",
    secureTextEntry: true,
    textContextType: "password",
    autoCorrect: false,
    name: "password"
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    accessibilityLabel: "sign-in-button",
    isDisabled: isAsyncProcessInProgress,
    title: "Sign in",
    backgroundColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme = currentTheme.theme) === null || _currentTheme$theme === void 0 ? void 0 : _currentTheme$theme.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme2 = currentTheme.theme) === null || _currentTheme$theme2 === void 0 ? void 0 : _currentTheme$theme2.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }]
  })), /*#__PURE__*/React__default.createElement(View, {
    style: styles$4.buttonContainer
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme3 = currentTheme.theme) === null || _currentTheme$theme3 === void 0 ? void 0 : _currentTheme$theme3.small_font_size
    },
    wrapperStyle: styles$4.separatorLabel,
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme4 = currentTheme.theme) === null || _currentTheme$theme4 === void 0 ? void 0 : _currentTheme$theme4.mediumGrey,
    preventTranslation: true
  }, "Forgot your password?")), /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme5 = currentTheme.theme) === null || _currentTheme$theme5 === void 0 ? void 0 : _currentTheme$theme5.small_font_size,
      textAlign: "center"
    },
    wrapperStyle: {
      alignSelf: "center",
      marginTop: 10
    },
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme6 = currentTheme.theme) === null || _currentTheme$theme6 === void 0 ? void 0 : _currentTheme$theme6.mediumGrey,
    preventTranslation: true,
    fontWeight: "bold"
  }, "Please click", " ", /*#__PURE__*/React__default.createElement(AppText, {
    isClickable: !isAsyncProcessInProgress,
    preventTranslation: true,
    onPress: showForgotPassword,
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme7 = currentTheme.theme) === null || _currentTheme$theme7 === void 0 ? void 0 : _currentTheme$theme7.primary,
    fontWeight: "bold",
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme8 = currentTheme.theme) === null || _currentTheme$theme8 === void 0 ? void 0 : _currentTheme$theme8.small_font_size
    }
  }, "here"), " ", "to recover your password"));
};
const styles$4 = StyleSheet.create({
  container: {
    width: "95%",
    maxWidth: 400
  },
  buttonContainer: {
    paddingTop: 30,
    justifyContent: "center",
    flexDirection: "row"
  }
});
SignInInputs.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "SignInInputs"
};

// const validationSchema = Yup.object().shape({
//   // username: Yup.string().required().label("Username"),
//   email: Yup.string().required().email().label("Email"),
//   password: Yup.string().required().min(4).label("Password"),
// });

const validationSchemaConfirmation = Yup.object().shape({
  authCode: Yup.string().required().matches(/^[0-9]+$/, "Must be only digits").min(6, "Must be exactly 6 digits").max(6, "Must be exactly 6 digits").label("Confirmation code"),
  email: Yup.string().email().label("Email")
});
const SignUp = props => {
  var _currentTheme$theme, _currentTheme$theme2, _currentTheme$theme3, _currentTheme$theme4, _currentTheme$theme5, _currentTheme$theme6, _currentTheme$theme7, _currentTheme$theme8, _currentTheme$theme9, _currentTheme$theme10, _currentTheme$theme11, _currentTheme$theme12, _currentTheme$theme13, _currentTheme$theme14, _currentTheme$theme15, _currentTheme$theme16, _currentTheme$theme17, _currentTheme$theme18, _currentTheme$theme19, _currentTheme$theme20;
  const AppToasty = useToast();
  const [stage, setStage] = useState(0);
  const [isSignUpInProgress, setSignUpInProgress] = useState(false);
  const [isConfirmationInProgress, setConfirmationInProgress] = useState(false);
  const [isForcedEmail, setForcedEmail] = useState(false);
  const [username, setUsername] = useState(null);
  const [email, setEmail] = useState(null);
  const [lastResortEmail, setLastResortEmail] = useState(null);
  const {
    t
  } = useTranslation();
  const {
    currentTheme
  } = useTheme();
  function decrypt(input) {
    var key = "1.0"; // Use the same secret key that was used to encrypt
    // Decode the Base64 input string before decryption
    var decodedInput = atob(input);
    var output = [];
    for (var i = 0; i < decodedInput.length; i++) {
      var charCode = decodedInput.charCodeAt(i) ^ key[i % key.length].charCodeAt(0);
      output.push(String.fromCharCode(charCode));
    }
    return output.join("");
  }
  // check if the url ends with code_confirmation and if so, set the stage to 1
  React__default.useEffect(() => {
    const currentURL = window.location.href;
    if (currentURL.includes("code_confirmation")) {
      var _currentURL$split;
      setStage(1);
      setEmail(decrypt(currentURL === null || currentURL === void 0 ? void 0 : (_currentURL$split = currentURL.split("code_confirmation/")) === null || _currentURL$split === void 0 ? void 0 : _currentURL$split.pop()));
    }
  }, [window.location.href]);
  const signUp = async values => {
    if (isSignUpInProgress) return AppToasty.show(t("SignUpInProgressCaption"), {
      placement: "top",
      type: "warning"
    });
    setSignUpInProgress(true);
    const {
      password,
      email
    } = values;
    try {
      await Auth.signUp({
        username: email,
        password,
        attributes: {
          email
        }
      });
      AppToasty.show(t("SignUpCaption"), {
        placement: "top",
        type: "success"
      });
      console.log("successful sign up..");
      setStage(1);
      setUsername(email);
      setEmail(email);
    } catch (err) {
      AppToasty.show(t("SignUpFailedCaption") + (err === null || err === void 0 ? void 0 : err.message), {
        placement: "top",
        type: "danger"
      });
      console.error("error signing up...", err);
      setSignUpInProgress(false);
    }
  };
  const confirmSignUp = async values => {
    if (isConfirmationInProgress) return AppToasty.show(t("SignUpInProgressCaption"), {
      placement: "top",
      type: "warning"
    });
    setConfirmationInProgress(true);
    const {
      authCode
    } = values;
    const username = email || values.email;
    const code = authCode;
    try {
      await Auth.confirmSignUp(username, code);
      AppToasty.show(t("SignUpSuccessCaption"), {
        placement: "top",
        type: "success"
      });
      setTimeout(() => {
        window.location.href = "/";
      }, 1000);
    } catch (err) {
      AppToasty.show(t("SignUpFailedCaption") + (err === null || err === void 0 ? void 0 : err.message), {
        placement: "top",
        type: "danger"
      });
      console.error("error signing up...", err);
      setConfirmationInProgress(false);
    }
  };
  return /*#__PURE__*/React__default.createElement(View, {
    style: styles$3.container
  }, stage === Number(0) && /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      username: "",
      email: "",
      password: ""
    },
    onSubmit: values => signUp(values),
    validationSchema: Validations.loginSchema
  }, /*#__PURE__*/React__default.createElement(AppText, {
    wrapperStyle: styles$3.titleLabel,
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme = currentTheme.theme) === null || _currentTheme$theme === void 0 ? void 0 : _currentTheme$theme.small_font_size
    },
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme2 = currentTheme.theme) === null || _currentTheme$theme2 === void 0 ? void 0 : _currentTheme$theme2.mediumGrey,
    fontWeight: "bold"
  }, "welcomeMessage"), /*#__PURE__*/React__default.createElement(AppFormField
  // leftIcon="email"
  // backgroundColor=""
  , {
    placeholder: "Email",
    autoCapitalize: "none",
    keyboardtype: "email-address",
    textContextType: "emailAddress",
    autoCorrect: false,
    name: "email"
  }), /*#__PURE__*/React__default.createElement(AppFormField
  // leftIcon="lock"
  // backgroundColor="rgba(245, 245, 245, 1)"
  , {
    placeholder: "Password",
    autoCapitalize: "none",
    secureTextEntry: true,
    textContextType: "password",
    autoCorrect: false,
    name: "password"
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    backgroundColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme3 = currentTheme.theme) === null || _currentTheme$theme3 === void 0 ? void 0 : _currentTheme$theme3.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    isDisabled: isSignUpInProgress,
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme4 = currentTheme.theme) === null || _currentTheme$theme4 === void 0 ? void 0 : _currentTheme$theme4.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }],
    title: "Sign up"
  }), /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme5 = currentTheme.theme) === null || _currentTheme$theme5 === void 0 ? void 0 : _currentTheme$theme5.small_font_size,
      textAlign: "center"
    },
    wrapperStyle: styles$3.separatorLabel,
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme6 = currentTheme.theme) === null || _currentTheme$theme6 === void 0 ? void 0 : _currentTheme$theme6.mediumGrey,
    preventTranslation: true,
    fontWeight: "bold"
  }, "Please click", " ", /*#__PURE__*/React__default.createElement(AppText, {
    isClickable: true,
    preventTranslation: true,
    onPress: () => {
      setStage(1);
      setForcedEmail(true);
    },
    wrapperStyle: {
      marginTop: 20,
      justifyContent: "center"
    },
    style: {
      fontWeight: "normal",
      color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme7 = currentTheme.theme) === null || _currentTheme$theme7 === void 0 ? void 0 : _currentTheme$theme7.primary,
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme8 = currentTheme.theme) === null || _currentTheme$theme8 === void 0 ? void 0 : _currentTheme$theme8.small_font_size,
      textAlign: "center"
    },
    fontWeight: "bold"
  }, "here"), " ", "to enter the confirmation code")), stage === Number(1) && /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      authCode: ""
    },
    onSubmit: values => confirmSignUp(values),
    validationSchema: validationSchemaConfirmation
  }, /*#__PURE__*/React__default.createElement(AppText, {
    wrapperStyle: styles$3.titleLabel,
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme9 = currentTheme.theme) === null || _currentTheme$theme9 === void 0 ? void 0 : _currentTheme$theme9.small_font_size
    },
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme10 = currentTheme.theme) === null || _currentTheme$theme10 === void 0 ? void 0 : _currentTheme$theme10.mediumGrey
  }, "insertCodeCaption"), isForcedEmail && /*#__PURE__*/React__default.createElement(AppFormField, {
    placeholder: "Email",
    autoCapitalize: "none"
    // keyboardtype="email-address"
    ,
    textContextType: "Name",
    autoCorrect: false,
    name: "email",
    onChange: v => {
      setLastResortEmail(v);
    }
  }), /*#__PURE__*/React__default.createElement(AppFormCodeInput, {
    placeholder: "Authorisation code",
    autoCapitalize: "none"
    // keyboardtype="email-address"
    ,
    autoCorrect: false,
    name: "authCode",
    rootStyle: {
      marginBottom: 40
    }
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    backgroundColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme11 = currentTheme.theme) === null || _currentTheme$theme11 === void 0 ? void 0 : _currentTheme$theme11.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme12 = currentTheme.theme) === null || _currentTheme$theme12 === void 0 ? void 0 : _currentTheme$theme12.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }],
    title: "Confirm Sign Up",
    isDisabled: isConfirmationInProgress
  }), /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme13 = currentTheme.theme) === null || _currentTheme$theme13 === void 0 ? void 0 : _currentTheme$theme13.small_font_size,
      textAlign: "center"
    },
    wrapperStyle: {
      marginTop: 20,
      justifyContent: "center"
    },
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme14 = currentTheme.theme) === null || _currentTheme$theme14 === void 0 ? void 0 : _currentTheme$theme14.mediumGrey,
    preventTranslation: true,
    fontWeight: "bold"
  }, /*#__PURE__*/React__default.createElement(AppText, {
    isClickable: true,
    preventTranslation: true,
    onPress: async () => {
      try {
        await Auth.resendSignUp(username || lastResortEmail);
        AppToasty.show(t("SignUpEmailResentCaption"), {
          placement: "top",
          type: "success"
        });
      } catch (error) {
        AppToasty.show(t("SignUpEmailResentErrorCaption"), {
          placement: "top",
          type: "danger"
        });
      }
    },
    style: {
      fontWeight: "normal",
      color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme15 = currentTheme.theme) === null || _currentTheme$theme15 === void 0 ? void 0 : _currentTheme$theme15.primary,
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme16 = currentTheme.theme) === null || _currentTheme$theme16 === void 0 ? void 0 : _currentTheme$theme16.small_font_size
    },
    fontWeight: "bold"
  }, "Resend the confirmation code"), " "), /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme17 = currentTheme.theme) === null || _currentTheme$theme17 === void 0 ? void 0 : _currentTheme$theme17.small_font_size,
      textAlign: "center"
    },
    wrapperStyle: {
      marginTop: 20,
      justifyContent: "center"
    },
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme18 = currentTheme.theme) === null || _currentTheme$theme18 === void 0 ? void 0 : _currentTheme$theme18.mediumGrey,
    preventTranslation: true,
    fontWeight: "bold"
  }, "Please click", " ", /*#__PURE__*/React__default.createElement(AppText, {
    isClickable: true,
    preventTranslation: true,
    onPress: () => {
      setStage(0);
      setForcedEmail(false);
    },
    style: {
      fontWeight: "normal",
      color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme19 = currentTheme.theme) === null || _currentTheme$theme19 === void 0 ? void 0 : _currentTheme$theme19.primary,
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme20 = currentTheme.theme) === null || _currentTheme$theme20 === void 0 ? void 0 : _currentTheme$theme20.small_font_size
    },
    fontWeight: "bold"
  }, "here"), " ", "to go back to signup form")));
};
const styles$3 = StyleSheet.create({
  container: {
    width: "95%",
    maxWidth: 400
  },
  input: {
    backgroundColor: "#fcf3db",
    borderRadius: 30,
    height: 45
  },
  titleLabel: {
    marginBottom: 20,
    marginTop: 10,
    alignSelf: "center",
    maxWidth: "100%",
    fontWeight: "bold",
    textAlign: "center"
  }
});
SignUp.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "SignUp"
};

const SSO_SignInInputs = _ref => {
  var _currentTheme$theme, _currentTheme$theme2, _currentTheme$theme3, _currentTheme$theme4, _currentTheme$theme5, _currentTheme$theme6, _currentTheme$theme7, _currentTheme$theme8;
  let {
    onSignIn,
    toggleAuthType,
    isAsyncProcessInProgress,
    setIsAsyncProcessInProgress,
    setUser,
    setFederatedSignInProgress,
    onProviderNotFound,
    getSSOproviderName
  } = _ref;
  const AppToasty = useToast();
  const {
    t
  } = useTranslation();
  const {
    currentTheme
  } = useTheme();
  const signIn = async values => {
    if (isAsyncProcessInProgress) return;
    try {
      setIsAsyncProcessInProgress(true);
      setFederatedSignInProgress(true);
      // get the domain from an email
      const _domain = values.email.split("@")[1];
      const result = await getSSOproviderName(_domain);
      if (!(result !== null && result !== void 0 && result.providerName)) {
        setFederatedSignInProgress(false);
        onProviderNotFound();
        return;
      }
      // mapping between domain of the email and the existing providers on cognito
      // ssoProviderName
      Auth.federatedSignIn({
        provider: result === null || result === void 0 ? void 0 : result.providerName
      }).then(user => {
        console.log("fed_user", user);
      }).catch(err => {
        console.log("fed_user_login_error", err);
      });
    } catch (error) {
      console.log("Auth - error signing in...", error);
      AppToasty.show(t("SignInFailedCaption") + (error === null || error === void 0 ? void 0 : error.message), {
        placement: "top",
        type: "danger"
      });
    } finally {
      setIsAsyncProcessInProgress(false);
    }
  };
  return /*#__PURE__*/React__default.createElement(View, {
    style: styles$2.container
  }, /*#__PURE__*/React__default.createElement(AppForm, {
    initialValues: {
      username: "",
      email: "",
      password: ""
    },
    onSubmit: values => signIn(values),
    validationSchema: Validations.ssoSchema
  }, /*#__PURE__*/React__default.createElement(AppFormField, {
    accessibilityLabel: "email",
    accessible: true,
    placeholder: "Enter your work email",
    autoCapitalize: "none",
    keyboardtype: "email-address",
    textContextType: "emailAddress",
    autoCorrect: false,
    name: "email"
  }), /*#__PURE__*/React__default.createElement(AppSubmitButton, {
    accessibilityLabel: "sign-in-button",
    isDisabled: isAsyncProcessInProgress,
    title: "Sign in with SSO",
    backgroundColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme = currentTheme.theme) === null || _currentTheme$theme === void 0 ? void 0 : _currentTheme$theme.primary,
    btnContentStyle: {
      paddingLeft: 0,
      paddingRight: 0
    },
    style: [{
      height: 35,
      minWidth: 200,
      marginLeft: "auto",
      marginRight: "auto",
      marginTop: 20,
      shadowColor: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme2 = currentTheme.theme) === null || _currentTheme$theme2 === void 0 ? void 0 : _currentTheme$theme2.transparent,
      marginHorizontal: 10
    }]
    // btnLabelStyle={{ fontSize: 16 }}
    ,
    wrapperStyle: [{
      width: "fit-content",
      alignSelf: "center",
      minWidth: 150
    }]
  })), /*#__PURE__*/React__default.createElement(View, {
    style: styles$2.buttonContainer
  }, /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme3 = currentTheme.theme) === null || _currentTheme$theme3 === void 0 ? void 0 : _currentTheme$theme3.small_font_size
    },
    wrapperStyle: styles$2.separatorLabel,
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme4 = currentTheme.theme) === null || _currentTheme$theme4 === void 0 ? void 0 : _currentTheme$theme4.mediumGrey,
    preventTranslation: true
  }, "Access your account with your work email using SSO"), /*#__PURE__*/React__default.createElement(AppText, {
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme5 = currentTheme.theme) === null || _currentTheme$theme5 === void 0 ? void 0 : _currentTheme$theme5.small_font_size,
      textAlign: "center"
    },
    wrapperStyle: {
      alignSelf: "center",
      marginTop: 10
    },
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme6 = currentTheme.theme) === null || _currentTheme$theme6 === void 0 ? void 0 : _currentTheme$theme6.mediumGrey,
    preventTranslation: true,
    fontWeight: "bold"
  }, "Please click", " ", /*#__PURE__*/React__default.createElement(AppText, {
    isClickable: !isAsyncProcessInProgress,
    preventTranslation: true,
    onPress: () => toggleAuthType("showSignIn"),
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme7 = currentTheme.theme) === null || _currentTheme$theme7 === void 0 ? void 0 : _currentTheme$theme7.primary,
    fontWeight: "bold",
    style: {
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme8 = currentTheme.theme) === null || _currentTheme$theme8 === void 0 ? void 0 : _currentTheme$theme8.small_font_size
    }
  }, "here"), " ", "to go back to the sign in page.")));
};
const styles$2 = StyleSheet.create({
  container: {
    maxWidth: 400
  },
  buttonContainer: {
    paddingTop: 30,
    justifyContent: "center",
    flexDirection: "column"
  }
});
SSO_SignInInputs.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "SSO_SignInInputs"
};

const BaseContentSection = _ref => {
  let {
    iconApple,
    iconEmail,
    iconFacebook,
    iconMicrosoft,
    iconGoogle,
    iconSignIn,
    showSignUp,
    showForgotPassword,
    showSignInInputs,
    showSignIn,
    showSSO_SignInInputs,
    toggleAuthType,
    onSignIn,
    isAsyncProcessInProgress,
    setIsAsyncProcessInProgress,
    isSignInWithGoogleEnabled,
    isSignInWithAppleEnabled,
    isSignInWithFacebookEnabled,
    AuthButton,
    showForgotPasswordNoCode,
    user,
    setUser,
    setFederatedSignInProgress,
    isSSOwithAzureAD,
    getSSOproviderName,
    onProviderNotFound
  } = _ref;
  const dimensions = useWindowDimensions();
  const [isLargeScreen, setLargeScreen] = useState((dimensions === null || dimensions === void 0 ? void 0 : dimensions.width) >= 768);
  useEffect(() => {
    const _largeScreen = (dimensions === null || dimensions === void 0 ? void 0 : dimensions.width) >= 768;
    setLargeScreen(_largeScreen);
  }, [dimensions === null || dimensions === void 0 ? void 0 : dimensions.width]);
  return /*#__PURE__*/React__default.createElement(View, {
    style: {
      // maxWidth: isLargeScreen ? 450 : 320,
      minWidth: isLargeScreen ? 350 : 320,
      flex: 1
    }
  }, showSignIn && /*#__PURE__*/React__default.createElement(SignIn, {
    isSignInWithGoogleEnabled: isSignInWithGoogleEnabled,
    isSignInWithAppleEnabled: isSignInWithAppleEnabled,
    isSignInWithFacebookEnabled: isSignInWithFacebookEnabled,
    iconApple: iconApple,
    iconEmail: iconEmail,
    iconFacebook: iconFacebook,
    iconGoogle: iconGoogle,
    iconSignIn: iconSignIn,
    iconMicrosoft: iconMicrosoft,
    toggleAuthType: toggleAuthType,
    updateAuth: () => toggleAuthType("mainNav"),
    onProviderNotFound: onProviderNotFound,
    AuthButton: AuthButton,
    setUser: setUser,
    setFederatedSignInProgress: setFederatedSignInProgress,
    isSSOwithAzureAD: isSSOwithAzureAD,
    getSSOproviderName: getSSOproviderName
  }), showSignInInputs && /*#__PURE__*/React__default.createElement(SignInInputs, {
    onSignIn: onSignIn,
    toggleAuthType: toggleAuthType,
    isAsyncProcessInProgress: isAsyncProcessInProgress,
    setIsAsyncProcessInProgress: setIsAsyncProcessInProgress,
    AuthButton: AuthButton,
    setUser: setUser
  }), showSSO_SignInInputs && /*#__PURE__*/React__default.createElement(SSO_SignInInputs, {
    onSignIn: onSignIn,
    toggleAuthType: toggleAuthType,
    isAsyncProcessInProgress: isAsyncProcessInProgress,
    setIsAsyncProcessInProgress: setIsAsyncProcessInProgress,
    AuthButton: AuthButton,
    setUser: setUser,
    setFederatedSignInProgress: setFederatedSignInProgress,
    getSSOproviderName: getSSOproviderName,
    onProviderNotFound: onProviderNotFound
  }), showSignUp && /*#__PURE__*/React__default.createElement(SignUp, {
    toggleAuthType: toggleAuthType,
    AuthButton: AuthButton
  }), (showForgotPassword || showForgotPasswordNoCode) && /*#__PURE__*/React__default.createElement(ForgotPassword, {
    toggleAuthType: toggleAuthType,
    AuthButton: AuthButton,
    showCode: !showForgotPasswordNoCode,
    user: user
  }));
};
BaseContentSection.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "BaseContentSection"
};

const BaseFooterSection = _ref => {
  var _currentTheme$theme, _currentTheme$theme2;
  let {
    showSignUp,
    showForgotPassword,
    showSignInInputs,
    toggleAuthType,
    isAsyncProcessInProgress
  } = _ref;
  const {
    currentTheme
  } = useTheme();
  return /*#__PURE__*/React__default.createElement(View, {
    style: {
      position: "relative",
      paddingTop: 30
    }
  }, (showSignInInputs || showSignUp || showForgotPassword) && /*#__PURE__*/React__default.createElement(AppText, {
    leftIcon: AppAssets.icons.back_icon,
    color: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme = currentTheme.theme) === null || _currentTheme$theme === void 0 ? void 0 : _currentTheme$theme.primary,
    style: {
      ...styles$1.bottomMessageHighlight,
      fontSize: currentTheme === null || currentTheme === void 0 ? void 0 : (_currentTheme$theme2 = currentTheme.theme) === null || _currentTheme$theme2 === void 0 ? void 0 : _currentTheme$theme2.small_font_size
    },
    isClickable: !isAsyncProcessInProgress,
    onPress: () => toggleAuthType("showSignIn"),
    wrapperStyle: {
      paddingBottom: 10
    },
    iconSize: 16,
    fontWeight: "bold"
  }, "Back to sign in options"));
};
const styles$1 = StyleSheet.create({
  bottomMessageHighlight: {
    // color: "#00cfd0",
    // textDecorationStyle: "solid",
    // paddingLeft: 10,
    // paddingBottom: 40,
  }
});
BaseFooterSection.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "BaseFooterSection"
};

Dimensions.get("window");
const BaseHeaderSection = _ref => {
  let {
    showSignUp,
    showForgotPassword,
    showSignInInputs,
    toggleAuthType,
    brandImage
  } = _ref;
  useState(null);
  return /*#__PURE__*/React__default.createElement(Image, {
    style: styles.logo,
    resizeMode: "contain",
    source: brandImage
  });
};
const styles = StyleSheet.create({
  logo: {
    height: 50,
    width: 200,
    margin: "auto",
    marginBottom: 10,
    resizeMode: "contain"
  }
});
BaseHeaderSection.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "BaseHeaderSection"
};

const AuthScreen = _ref => {
  let {
    brandImage,
    bgVideo,
    config,
    iconApple,
    iconEmail,
    iconFacebook,
    iconGoogle,
    iconSignIn,
    iconMicrosoft,
    onSignIn,
    isSignInWithGoogleEnabled,
    isSignInWithAppleEnabled,
    isSignInWithFacebookEnabled,
    AuthButton = AppButton,
    setFederatedSignInProgress,
    isSSOwithAzureAD,
    getSSOproviderName = () => {},
    onProviderNotFound = () => {}
  } = _ref;
  const dimensions = useWindowDimensions();
  const [isLargeScreen, setLargeScreen] = useState((dimensions === null || dimensions === void 0 ? void 0 : dimensions.width) >= 768);
  useEffect(() => {
    const _largeScreen = (dimensions === null || dimensions === void 0 ? void 0 : dimensions.width) >= 768;
    setLargeScreen(_largeScreen);
  }, [dimensions === null || dimensions === void 0 ? void 0 : dimensions.width]);
  const [formType, setFormType] = useState("showSignIn");
  useState(false);
  const [user, setUser] = useState(null);
  const [isAsyncProcessInProgress, setIsAsyncProcessInProgress] = useState(false);
  Auth.configure(config);
  const toggleAuthType = formType => {
    setFormType(formType);
  };
  // get the exact path of the current screen
  const currentURL = window.location.href;
  useEffect(() => {
    if (currentURL.split("/").includes("signUp") || currentURL.split("/").includes("code_confirmation")) {
      setFormType("showSignUp");
    }
  }, [currentURL]);
  const showSignIn = formType === "showSignIn";
  const showSignInInputs = formType === "showSignInInputs";
  const showSignUp = formType === "showSignUp";
  const showForgotPassword = formType === "showForgotPassword";
  const showForgotPasswordNoCode = formType === "showForgotPasswordNoCode";
  const showSSO_SignInInputs = formType === "showSSO_SignInInputs";
  useTheme();
  const styles = styleGenerator();
  return /*#__PURE__*/React__default.createElement(KeyboardAvoidingView, {
    style: styles.container,
    behavior: Platform.OS == "ios" ? "padding" : "height"
  }, /*#__PURE__*/React__default.createElement(View, {
    style: {
      ...styles.form,
      width: isLargeScreen ? "50%" : "100%"
    }
  }, /*#__PURE__*/React__default.createElement(BaseHeaderSection, {
    brandImage: brandImage
  }), /*#__PURE__*/React__default.createElement(BaseContentSection, {
    iconApple: iconApple,
    iconEmail: iconEmail,
    iconFacebook: iconFacebook,
    iconGoogle: iconGoogle,
    iconMicrosoft: iconMicrosoft,
    iconSignIn: iconSignIn,
    isSignInWithGoogleEnabled: isSignInWithGoogleEnabled,
    isSignInWithAppleEnabled: isSignInWithAppleEnabled,
    isSignInWithFacebookEnabled: isSignInWithFacebookEnabled,
    toggleAuthType: toggleAuthType,
    onSignIn: onSignIn,
    showSignIn: showSignIn,
    showForgotPassword: showForgotPassword,
    showSignInInputs: showSignInInputs,
    showSignUp: showSignUp,
    isAsyncProcessInProgress: isAsyncProcessInProgress,
    showSSO_SignInInputs: showSSO_SignInInputs,
    setIsAsyncProcessInProgress: setIsAsyncProcessInProgress,
    AuthButton: AuthButton,
    showForgotPasswordNoCode: showForgotPasswordNoCode,
    setUser: setUser,
    getSSOproviderName: getSSOproviderName,
    onProviderNotFound: onProviderNotFound,
    user: user,
    setFederatedSignInProgress: setFederatedSignInProgress,
    isSSOwithAzureAD: isSSOwithAzureAD
  }), /*#__PURE__*/React__default.createElement(BaseFooterSection, {
    toggleAuthType: toggleAuthType,
    showSignIn: showSignIn,
    showForgotPassword: showForgotPassword,
    showSignInInputs: showSignInInputs,
    showSignUp: showSignUp,
    isAsyncProcessInProgress: isAsyncProcessInProgress
  })), isLargeScreen && /*#__PURE__*/React__default.createElement("div", {
    style: {
      width: isLargeScreen ? "50%" : "100%",
      height: "100%",
      display: "flex",
      alignItems: "center",
      justifyContent: "center"
      // backgroundColor: "#000"
    }
  }, /*#__PURE__*/React__default.createElement("video", {
    style: {
      width: "100%",
      maxWidth: "500px",
      height: "auto",
      aspectRatio: "676/584",
      objectFit: "cover",
      overflow: "hidden",
      borderRadius: "10px",
      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
      marginRight: "20px",
      marginLeft: "10px"
    },
    src: bgVideo,
    autoPlay: true,
    muted: true,
    playsInline: true,
    onEnded: e => {
      setTimeout(() => {
        e.target.currentTime = 0;
        e.target.play();
      }, 1000);
    }
  })));
};
const styleGenerator = () => StyleSheet.create({
  container: {
    alignItems: "center",
    // backgroundColor: "#0B1019",
    flex: 1,
    flexDirection: "row",
    height: "100%"
  },
  bg: {
    height: "100%"
  },
  form: {
    alignItems: "center",
    flexDirection: "column"
  }
});
AuthScreen.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "AuthScreen",
  "props": {
    "AuthButton": {
      "defaultValue": {
        "value": "AppButton",
        "computed": true
      },
      "required": false
    },
    "getSSOproviderName": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "required": false
    },
    "onProviderNotFound": {
      "defaultValue": {
        "value": "() => {}",
        "computed": false
      },
      "required": false
    }
  }
};

var _window;
const isElectron = !!((_window = window) !== null && _window !== void 0 && _window.electronAPI);
const getCurrentAuthenticatedUser = async () => {
  if (isElectron) {
    var _window$electronAPI;
    // --- Electron Flow ---

    if (!((_window$electronAPI = window.electronAPI) !== null && _window$electronAPI !== void 0 && _window$electronAPI.getUserClaims)) {
      return null;
    }
    try {
      // This IPC handler should retrieve stored tokens and return the decoded ID token claims
      // It should also handle expiry checks internally.
      const claims = await window.electronAPI.getUserClaims();
      if (!claims) {}
      return claims; // Return the claims object or null
    } catch (error) {
      console.error("AuthUtil: Error invoking electronAPI.getUserClaims:", error);
      return null;
    }
  } else {
    // --- Web Flow (using Amplify) ---

    try {
      var _user$signInUserSessi, _user$signInUserSessi2;
      // Use the core Amplify Auth directly
      // Add bypassCache option for potentially fresher data, though usually not needed here
      const user = await Auth$1.currentAuthenticatedUser({
        bypassCache: false
      });

      // Extract the ID token payload which contains the claims/attributes
      // This provides data consistent with what the Electron flow returns
      const claims = user === null || user === void 0 ? void 0 : (_user$signInUserSessi = user.signInUserSession) === null || _user$signInUserSessi === void 0 ? void 0 : (_user$signInUserSessi2 = _user$signInUserSessi.idToken) === null || _user$signInUserSessi2 === void 0 ? void 0 : _user$signInUserSessi2.payload;
      if (!claims) {
        console.warn("AuthUtil: Amplify user authenticated but failed to get ID token payload.");
        // Fallback? Maybe return basic user attributes if available?
        // For consistency, return null if payload isn't there.
        return null;
      }
      console.log("AuthUtil: Extracted claims from Amplify user session.");
      return claims;
    } catch (error) {
      // Amplify typically throws an error string 'No current user' if not logged in
      console.log("AuthUtil: Amplify Auth.currentAuthenticatedUser failed (likely not logged in):", error);
      return null; // Return null if no session / not authenticated
    }
  }
};

const getCurrentSession = () => {
  if (isElectron) {
    var _window$electronAPI2;
    // --- Electron Flow ---

    if (!((_window$electronAPI2 = window.electronAPI) !== null && _window$electronAPI2 !== void 0 && _window$electronAPI2.getAccessToken)) {
      return null;
    }
    try {
      return window.electronAPI.getIdToken().then(result_ => {
        return {
          idToken: {
            jwtToken: result_
          }
        };
      });

      // This Electron API call should handle refresh internally (as implemented in main.js)
    } catch (error) {
      console.log("AuthUtil: Error invoking electronAPI.getAccessToken:", error);
      return null;
    }
  } else {
    // --- Web Flow (using Amplify) ---
    try {
      // Use the core Amplify Auth directly
      return Auth$1.currentSession();
      // session object structure might vary slightly, ensure paths are correct
      // const token = session?.getAccessToken()?.getJwtToken();
      // if (!token) {
      //     console.warn('AuthUtil: Amplify session valid but no access token JWT found.');
      // }
      // return token;
    } catch (error) {
      // Amplify typically throws an error string 'No current user' if not logged in
      console.log("AuthUtil: Amplify Auth.currentSession failed (likely not logged in):", error);
      return null; // Return null if no session / not authenticated
    }
  }
};

/**
 * Gets the current valid ID token JWT string, handling both Electron and Web flows.
 * Returns the JWT string or null if not authenticated or an error occurs.
 * NOTE: This does NOT trigger refresh in Electron; expiry is checked.
 */
const getCurrentIdToken = async () => {
  if (isElectron) {
    var _window$electronAPI3;
    // --- Electron Flow ---
    if (!((_window$electronAPI3 = window.electronAPI) !== null && _window$electronAPI3 !== void 0 && _window$electronAPI3.getIdToken)) {
      console.log("AuthUtil: window.electronAPI.getIdToken is not available!");
      return null;
    }
    try {
      const token = await window.electronAPI.getIdToken(); // Calls the new handler
      if (!token) {
        console.log("AuthUtil: Electron API returned null ID token (not logged in or expired).");
      }
      return token;
    } catch (error) {
      console.error("AuthUtil: Error invoking electronAPI.getIdToken:", error);
      return null;
    }
  } else {
    // --- Web Flow (using Amplify) ---
    try {
      var _session$getIdToken;
      const session = await Auth$1.currentSession();
      const token = session === null || session === void 0 ? void 0 : (_session$getIdToken = session.getIdToken()) === null || _session$getIdToken === void 0 ? void 0 : _session$getIdToken.getJwtToken(); // Get ID Token JWT
      if (!token) {
        console.log("AuthUtil: Amplify session valid but no ID token JWT found.");
      }
      return token;
    } catch (error) {
      console.log("AuthUtil: Amplify Auth.currentSession failed (likely not logged in):", error);
      return null;
    }
  }
};
var AppAuthScreen = {
  AppAuthScreen: AuthScreen,
  useAuth,
  Auth: Auth$1,
  getCurrentSession,
  getCurrentIdToken,
  getCurrentAuthenticatedUser
};

export { AppAuthScreen as A, AuthScreen as a, getCurrentIdToken as g, useAuth as u };
